import React, { useState } from 'react';
import { Button, ButtonGroup, Dropdown, Form } from 'react-bootstrap';
import { Filter, Hash, Layers, Tag, Flag, ListFilter, Bell, Repeat, Calendar, AlertCircle, Menu } from 'lucide-react';
// BoardConfigDialog import removed as it is no longer used

const TasksToolbar = ({ project, board, onManageTags, tags = [], projects = [], taskGroups = [], filterTags = [], setFilterTags, filterProjectIds = [], setFilterProjectIds, filterCategoryIds = [], setFilterCategoryIds, filterPriorities = [], setFilterPriorities, filterAttributes = [], setFilterAttributes, onUpdateProject, onUpdateBoard, auth, workspaceId, onToggleSidebar, members }) => {
    const title = board ? board.name : project?.name;
    const canEditTitle = !!project; // Only projects for now to avoid sync issues

    const [isEditingName, setIsEditingName] = React.useState(false);
    const [name, setName] = React.useState(title);

    React.useEffect(() => {
        setName(title);
    }, [title]);

    const handleSaveName = async () => {
        if (name.trim() && name !== title && canEditTitle) {
            await onUpdateProject(project.id, { name });
        }
        setIsEditingName(false);
    };

    const toggleTagFilter = (tagId) => {
        if (filterTags.includes(tagId)) {
            setFilterTags(filterTags.filter(id => id !== tagId));
        } else {
            setFilterTags([...filterTags, tagId]);
        }
    };

    return (
        <div className="p-3 p-md-4 border-bottom d-flex flex-wrap flex-md-nowrap justify-content-between align-items-center gap-3">
            <div className="d-flex align-items-center gap-2">
                <Button variant="link" className="p-0 text-dark d-md-none" onClick={onToggleSidebar}>
                    <Menu size={24} />
                </Button>
                {isEditingName ? (
                    <Form.Control
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        onBlur={handleSaveName}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveName();
                            if (e.key === 'Escape') {
                                setName(title);
                                setIsEditingName(false);
                            }
                        }}
                        autoFocus
                        className="fw-bold fs-3 border-0 p-0 shadow-none"
                        style={{ width: 'auto', maxWidth: '400px' }}
                    />
                ) : (
                    <h3
                        className={`m-0 fw-bold ${canEditTitle ? 'cursor-pointer hover-opacity-75' : ''}`}
                        onClick={() => canEditTitle && setIsEditingName(true)}
                        title={canEditTitle ? "Click to rename project" : "Board View"}
                    >
                        {title}
                    </h3>
                )}
            </div>

            <div className="d-flex align-items-center gap-2 flex-wrap">

                {/* Project Filter */}
                {projects && projects.length > 0 && (
                    <Dropdown autoClose="outside">
                        <Dropdown.Toggle variant={filterProjectIds && filterProjectIds.length > 0 ? "primary" : "outline-secondary"} size="sm" className="d-flex align-items-center gap-2">
                            <Hash size={16} />
                            Project
                            {filterProjectIds && filterProjectIds.length > 0 && <span className="badge bg-light text-dark rounded-pill ms-1">{filterProjectIds.length}</span>}
                        </Dropdown.Toggle>

                        <Dropdown.Menu style={{ minWidth: '200px', maxHeight: '300px', overflowY: 'auto' }}>
                            <Dropdown.Header>Filter by Project</Dropdown.Header>
                            {projects.map(proj => (
                                <Dropdown.Item key={proj.id} onClick={(e) => {
                                    e.preventDefault();
                                    const newIds = filterProjectIds.includes(proj.id)
                                        ? filterProjectIds.filter(id => id !== proj.id)
                                        : [...filterProjectIds, proj.id];
                                    setFilterProjectIds(newIds);
                                }}>
                                    <Form.Check
                                        type="checkbox"
                                        id={`filter-proj-${proj.id}`}
                                        label={proj.name}
                                        checked={filterProjectIds.includes(proj.id)}
                                        onChange={() => { }}
                                        style={{ pointerEvents: 'none' }}
                                    />
                                </Dropdown.Item>
                            ))}
                            {filterProjectIds.length > 0 && (
                                <>
                                    <Dropdown.Divider />
                                    <Dropdown.Item onClick={() => setFilterProjectIds([])} className="text-danger small">
                                        Clear Project Filters
                                    </Dropdown.Item>
                                </>
                            )}
                        </Dropdown.Menu>
                    </Dropdown>
                )}

                {/* Section Filter */}
                {taskGroups && (
                    <Dropdown autoClose="outside">
                        <Dropdown.Toggle variant={filterCategoryIds && filterCategoryIds.length > 0 ? "primary" : "outline-secondary"} size="sm" className="d-flex align-items-center gap-2">
                            <Layers size={16} />
                            Section
                            {filterCategoryIds && filterCategoryIds.length > 0 && <span className="badge bg-light text-dark rounded-pill ms-1">{filterCategoryIds.length}</span>}
                        </Dropdown.Toggle>

                        <Dropdown.Menu style={{ minWidth: '220px', maxHeight: '350px', overflowY: 'auto' }}>
                            <Dropdown.Header>Filter by Section</Dropdown.Header>
                            {/* Uncategorized Option */}
                            <Dropdown.Item onClick={(e) => {
                                e.preventDefault();
                                const id = 'uncategorized';
                                const newIds = filterCategoryIds.includes(id)
                                    ? filterCategoryIds.filter(cid => cid !== id)
                                    : [...filterCategoryIds, id];
                                setFilterCategoryIds(newIds);
                            }}>
                                <Form.Check
                                    type="checkbox"
                                    id={`filter-cat-uncategorized`}
                                    label="Uncategorized"
                                    checked={filterCategoryIds.includes('uncategorized')}
                                    onChange={() => { }}
                                    style={{ pointerEvents: 'none' }}
                                />
                            </Dropdown.Item>

                            {taskGroups.length > 0 && <Dropdown.Divider />}

                            {/* Group sections by project in board view */}
                            {board && projects.length > 0 ? (
                                (() => {
                                    const groupsByProject = {};
                                    const ungrouped = [];
                                    taskGroups.forEach(group => {
                                        const proj = projects.find(p => p.id === group.project_id);
                                        if (proj) {
                                            if (!groupsByProject[proj.id]) groupsByProject[proj.id] = { project: proj, groups: [] };
                                            groupsByProject[proj.id].groups.push(group);
                                        } else {
                                            ungrouped.push(group);
                                        }
                                    });

                                    return (
                                        <>
                                            {Object.values(groupsByProject).map(({ project: proj, groups }) => (
                                                <React.Fragment key={proj.id}>
                                                    <Dropdown.Header className="text-uppercase small fw-bold" style={{ fontSize: '0.65rem' }}>{proj.name}</Dropdown.Header>
                                                    {groups.map(group => (
                                                        <Dropdown.Item key={group.id} onClick={(e) => {
                                                            e.preventDefault();
                                                            const newIds = filterCategoryIds.includes(group.id)
                                                                ? filterCategoryIds.filter(id => id !== group.id)
                                                                : [...filterCategoryIds, group.id];
                                                            setFilterCategoryIds(newIds);
                                                        }}>
                                                            <Form.Check
                                                                type="checkbox"
                                                                id={`filter-group-${group.id}`}
                                                                label={group.name}
                                                                checked={filterCategoryIds.includes(group.id)}
                                                                onChange={() => { }}
                                                                style={{ pointerEvents: 'none' }}
                                                            />
                                                        </Dropdown.Item>
                                                    ))}
                                                </React.Fragment>
                                            ))}
                                            {ungrouped.map(group => (
                                                <Dropdown.Item key={group.id} onClick={(e) => {
                                                    e.preventDefault();
                                                    const newIds = filterCategoryIds.includes(group.id)
                                                        ? filterCategoryIds.filter(id => id !== group.id)
                                                        : [...filterCategoryIds, group.id];
                                                    setFilterCategoryIds(newIds);
                                                }}>
                                                    <Form.Check
                                                        type="checkbox"
                                                        id={`filter-group-${group.id}`}
                                                        label={group.name}
                                                        checked={filterCategoryIds.includes(group.id)}
                                                        onChange={() => { }}
                                                        style={{ pointerEvents: 'none' }}
                                                    />
                                                </Dropdown.Item>
                                            ))}
                                        </>
                                    );
                                })()
                            ) : (
                                taskGroups.map(group => (
                                    <Dropdown.Item key={group.id} onClick={(e) => {
                                        e.preventDefault();
                                        const newIds = filterCategoryIds.includes(group.id)
                                            ? filterCategoryIds.filter(id => id !== group.id)
                                            : [...filterCategoryIds, group.id];
                                        setFilterCategoryIds(newIds);
                                    }}>
                                        <Form.Check
                                            type="checkbox"
                                            id={`filter-group-${group.id}`}
                                            label={group.name}
                                            checked={filterCategoryIds.includes(group.id)}
                                            onChange={() => { }}
                                            style={{ pointerEvents: 'none' }}
                                        />
                                    </Dropdown.Item>
                                ))
                            )}

                            {filterCategoryIds.length > 0 && (
                                <>
                                    <Dropdown.Divider />
                                    <Dropdown.Item onClick={() => setFilterCategoryIds([])} className="text-danger small">
                                        Clear Section Filters
                                    </Dropdown.Item>
                                </>
                            )}
                        </Dropdown.Menu>
                    </Dropdown>
                )}

                {/* Priority Filter */}
                <Dropdown autoClose="outside">
                    <Dropdown.Toggle variant={filterPriorities && filterPriorities.length > 0 ? "primary" : "outline-secondary"} size="sm" className="d-flex align-items-center gap-2">
                        <Flag size={16} />
                        Priority
                        {filterPriorities && filterPriorities.length > 0 && <span className="badge bg-light text-dark rounded-pill ms-1">{filterPriorities.length}</span>}
                    </Dropdown.Toggle>

                    <Dropdown.Menu style={{ minWidth: '200px' }}>
                        <Dropdown.Header>Filter by Priority</Dropdown.Header>
                        {[
                            { label: 'High', value: 3, color: 'text-danger' },
                            { label: 'Medium', value: 2, color: 'text-warning' },
                            { label: 'Low', value: 1, color: 'text-secondary' }
                        ].map(p => (
                            <Dropdown.Item key={p.value} onClick={(e) => {
                                e.preventDefault();
                                const newPriorities = filterPriorities.includes(p.value)
                                    ? filterPriorities.filter(v => v !== p.value)
                                    : [...filterPriorities, p.value];
                                setFilterPriorities(newPriorities);
                            }}>
                                <Form.Check
                                    type="checkbox"
                                    id={`filter-priority-${p.value}`}
                                    label={<span className={p.color}>{p.label}</span>}
                                    checked={filterPriorities.includes(p.value)}
                                    onChange={() => { }}
                                    style={{ pointerEvents: 'none' }}
                                />
                            </Dropdown.Item>
                        ))}
                        {filterPriorities.length > 0 && (
                            <>
                                <Dropdown.Divider />
                                <Dropdown.Item onClick={() => setFilterPriorities([])} className="text-danger small">
                                    Clear Priority Filters
                                </Dropdown.Item>
                            </>
                        )}
                    </Dropdown.Menu>
                </Dropdown>

                {/* Attribute Filters */}
                <Dropdown autoClose="outside">
                    <Dropdown.Toggle variant={filterAttributes && filterAttributes.length > 0 ? "primary" : "outline-secondary"} size="sm" className="d-flex align-items-center gap-2">
                        <ListFilter size={16} />
                        More
                        {filterAttributes && filterAttributes.length > 0 && <span className="badge bg-light text-dark rounded-pill ms-1">{filterAttributes.length}</span>}
                    </Dropdown.Toggle>

                    <Dropdown.Menu style={{ minWidth: '200px' }}>
                        <Dropdown.Header>Filter by Attributes</Dropdown.Header>
                        {[
                            { label: 'Has Reminders', value: 'hasReminders', icon: <Bell size={14} className="me-2 text-muted" /> },
                            { label: 'Is Recurring', value: 'isRecurring', icon: <Repeat size={14} className="me-2 text-muted" /> },
                            { label: 'Has Due Date', value: 'hasDueDate', icon: <Calendar size={14} className="me-2 text-muted" /> },
                            { label: 'Is Overdue', value: 'isOverdue', icon: <AlertCircle size={14} className="me-2 text-danger" /> }
                        ].map(attr => (
                            <Dropdown.Item key={attr.value} onClick={(e) => {
                                e.preventDefault();
                                const newAttributes = filterAttributes.includes(attr.value)
                                    ? filterAttributes.filter(v => v !== attr.value)
                                    : [...filterAttributes, attr.value];
                                setFilterAttributes(newAttributes);
                            }}>
                                <Form.Check
                                    type="checkbox"
                                    id={`filter-attr-${attr.value}`}
                                    label={<span className="d-flex align-items-center">{attr.icon}{attr.label}</span>}
                                    checked={filterAttributes.includes(attr.value)}
                                    onChange={() => { }}
                                    style={{ pointerEvents: 'none' }}
                                />
                            </Dropdown.Item>
                        ))}
                        {filterAttributes.length > 0 && (
                            <>
                                <Dropdown.Divider />
                                <Dropdown.Item onClick={() => setFilterAttributes([])} className="text-danger small">
                                    Clear Attribute Filters
                                </Dropdown.Item>
                            </>
                        )}
                    </Dropdown.Menu>
                </Dropdown>

                {/* Filter Dropdown (Tags) */}
                <Dropdown autoClose="outside">
                    <Dropdown.Toggle variant={filterTags.length > 0 ? "primary" : "outline-secondary"} size="sm" className="d-flex align-items-center gap-2">
                        <Tag size={16} />
                        Tags
                        {filterTags.length > 0 && <span className="badge bg-light text-dark rounded-pill ms-1">{filterTags.length}</span>}
                    </Dropdown.Toggle>

                    <Dropdown.Menu style={{ minWidth: '200px' }}>
                        <Dropdown.Header>Filter by Tags</Dropdown.Header>
                        {tags.length === 0 ? (
                            <div className="px-3 py-2 text-muted small">No tags available</div>
                        ) : (
                            tags.map(tag => (
                                <Dropdown.Item key={tag.id} onClick={(e) => { e.preventDefault(); toggleTagFilter(tag.id); }}>
                                    <Form.Check
                                        type="checkbox"
                                        id={`filter-tag-${tag.id}`}
                                        label={tag.name}
                                        checked={filterTags.includes(tag.id)}
                                        onChange={() => { }} // Handled by Dropdown.Item onClick
                                        style={{ pointerEvents: 'none' }} // Let the item handle click
                                    />
                                </Dropdown.Item>
                            ))
                        )}
                        {filterTags.length > 0 && (
                            <>
                                <Dropdown.Divider />
                                <Dropdown.Item onClick={() => setFilterTags([])} className="text-danger small">
                                    Clear Filters
                                </Dropdown.Item>
                            </>
                        )}
                    </Dropdown.Menu>
                </Dropdown>

                <Button variant="outline-secondary" size="sm" onClick={onManageTags}>
                    Manage Tags
                </Button>
            </div>
        </div >
    );
};

export default TasksToolbar;
